import { useState } from "react"
import { ArrowLeft, Loader2, Mail } from "lucide-react"

import AuraQuizEmail from "@/assets/img/aura/aura_quiz_email.webp"
import MoneyBackIcon from "@/assets/img/aura/money_back_icon.svg"
import { Button } from "@/components/ui/button"
import { cn } from "@/lib/utils"
import { AppRoutes } from "@/shared/lib/routes"
import { LegalLink } from "@/shared/components/legal-link"
import type { StepComponentProps } from "./step-contract"

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

/**
 * Aura email capture (Figma Email / LPR_Quiz_Aura_Email). Full-bleed hero of
 * the cupped-hands aura art, then title, field, Continue, and a legal strip.
 * Owns its CTA; `step.submitDelayMs` shows a loader before advancing.
 */
export function AuraEmailStep({
  step,
  value,
  onChange,
  onAdvance,
  onBack,
}: StepComponentProps) {
  const [submitting, setSubmitting] = useState(false)
  const [focused, setFocused] = useState(false)
  const [touched, setTouched] = useState(false)
  const email = typeof value === "string" ? value : ""
  const valid = EMAIL_RE.test(email)
  const active = focused || email.length > 0
  const showError = touched && email.length > 0 && !valid

  const submit = () => {
    if (submitting) return
    if (!valid) {
      setTouched(true)
      return
    }
    if (step.submitDelayMs) {
      setSubmitting(true)
      setTimeout(onAdvance, step.submitDelayMs)
    } else {
      onAdvance()
    }
  }

  return (
    <div
      className="flex h-dvh min-h-dvh flex-col overflow-hidden bg-dark-base text-purple-light"
      data-slot="aura-email-step"
    >
      <div className="relative flex min-h-0 flex-1 flex-col">
        <img
          src={AuraQuizEmail}
          alt=""
          className="pointer-events-none absolute top-0 left-1/2 z-0 w-full max-w-[500px] -translate-x-1/2 object-contain object-top"
        />
        <div className="absolute inset-x-0 top-0 flex h-14 items-center px-4">
          <button
            type="button"
            onClick={onBack}
            aria-label="Go back"
            className="-ml-2 flex size-10 items-center justify-center rounded-full text-purple-light transition-colors hover:bg-purple-light/10"
          >
            <ArrowLeft className="size-6" />
          </button>
        </div>
        <div className="relative mt-auto space-y-2 px-4 pb-6 text-center">
          <h1 className="font-display text-2xl font-medium leading-[normal] text-purple-light">{step.title}</h1>
          {step.subtitle && (
            <p className="text-base leading-[normal] text-purple-light/70">{step.subtitle}</p>
          )}
        </div>
      </div>

      <div className="relative z-10 flex shrink-0 flex-col gap-8">
        <div className="flex flex-col gap-6 px-4">
          <div>
            <label
              className={cn(
                "flex h-12 items-center gap-3 rounded-xl border bg-purple-light/10 px-4 transition-colors",
                showError ? "border-peach" : active ? "border-main-purple" : "border-transparent",
              )}
            >
              <Mail
                className={cn(
                  "size-6 shrink-0 transition-colors",
                  showError ? "text-peach" : active ? "text-main-purple" : "text-purple-light/40",
                )}
              />
              <input
                type="email"
                inputMode="email"
                autoComplete="email"
                value={email}
                placeholder={step.placeholder}
                onChange={(e) => onChange(e.target.value)}
                onFocus={() => setFocused(true)}
                onBlur={() => {
                  setFocused(false)
                  setTouched(true)
                }}
                className="h-full flex-1 bg-transparent text-base font-medium text-purple-light caret-main-purple outline-none placeholder:text-purple-light/40"
              />
            </label>
            {showError && (
              <p className="mt-1 text-center text-sm text-peach">Please enter a valid email</p>
            )}
          </div>

          {submitting ? (
            <div
              aria-busy="true"
              className="flex h-14 w-full items-center justify-center gap-2 rounded-full bg-purple-light/10 font-body text-base font-semibold text-purple-light/70"
            >
              <Loader2 className="size-6 animate-spin" />
              Continue
            </div>
          ) : (
            <Button variant="gradient" size="xl" onClick={submit}>
              Continue
            </Button>
          )}
        </div>

        {step.legalNote && (
          <div className="flex flex-col gap-2 bg-purple-light/10 p-4 pb-safe">
            <div className="flex items-center gap-3">
              <img src={MoneyBackIcon} alt="" className="size-5 shrink-0" />
              <p className="flex-1 text-xs text-purple-light/70">
                Your personal data is safe with us. We'll use your email for updates, receipts, and
                subscription details.
              </p>
            </div>
            <p className="pl-8 text-xs text-purple-light/70">
              We respect your privacy. By continuing, you agree to our{" "}
              <LegalLink to={AppRoutes.TERMS} className="text-purple-light underline">
                Terms &amp; Conditions
              </LegalLink>{" "}
              and{" "}
              <LegalLink to={AppRoutes.PRIVACY} className="text-purple-light underline">
                Privacy Policy
              </LegalLink>.
            </p>
          </div>
        )}
      </div>
    </div>
  )
}
